import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { ActionMenu, type ActionMenuEntry } from "@/components/admin/action-menu";
import { AlertBanner } from "@/components/admin/alert-banner";
import { AdminButton } from "@/components/admin/button";
import { Card } from "@/components/admin/card";
import { FormRow } from "@/components/admin/form";
import { AdminIcon } from "@/components/admin/icon";
import { PageHead } from "@/components/admin/page-head";
import { Tag } from "@/components/admin/tag";
import { useConfirm, useToast } from "@/components/ui/feedback";
import { Modal, ModalFooter } from "@/components/ui/modal";
import { extractErrorMessage } from "@/hooks/use-auth-api";
import { usePermissions } from "@/hooks/use-permissions";
import { useSegments } from "@/lib/customers/customers-hooks";
import {
	WA_STATUS_LABELS,
	type WaCampaign,
	whatsappApi,
} from "@/lib/marketing/whatsapp-api";

const CLE = ["marketing", "whatsapp"] as const;

const nombre = (n: number) => n.toLocaleString("fr-FR");
const francs = (n: number) => `${n.toLocaleString("fr-FR")} F`;

const dateHeure = (iso: string | null) =>
	iso
		? new Date(iso).toLocaleString("fr-FR", {
				day: "2-digit",
				month: "2-digit",
				hour: "2-digit",
				minute: "2-digit",
			})
		: "—";

/**
 * Campagnes WhatsApp Business.
 *
 * Le contenu n'est pas libre : hors fenêtre de vingt-quatre heures, Meta
 * n'accepte que des modèles qu'elle a approuvés. L'écran ne propose donc pas
 * d'éditeur de texte, mais le choix d'un modèle et de ses paramètres — ce qui
 * évite de rédiger un message que l'API refusera.
 */
export function AdminMarketingWhatsappPage() {
	const qc = useQueryClient();
	const toast = useToast();
	const confirm = useConfirm();
	const { hasAny } = usePermissions();
	const peutEcrire = hasAny("MARKETING.CMS.WRITE");
	const peutEnvoyer = hasAny("MARKETING.CAMPAIGN.SEND");

	const [creation, setCreation] = useState(false);

	const { data, isLoading } = useQuery({
		queryKey: [...CLE, "list"],
		queryFn: () => whatsappApi.list(),
	});
	const { data: stats } = useQuery({
		queryKey: [...CLE, "stats"],
		queryFn: () => whatsappApi.stats(),
	});

	const rafraichir = () => {
		void qc.invalidateQueries({ queryKey: CLE });
	};
	const echec = (e: unknown) => toast.error(extractErrorMessage(e));

	const creer = useMutation({
		mutationFn: (p: {
			name: string;
			templateName: string;
			templateParams?: string[];
			segmentId?: string;
		}) => whatsappApi.create(p),
		onSuccess: () => {
			toast.success("Campagne créée en brouillon");
			setCreation(false);
			rafraichir();
		},
		onError: echec,
	});

	const programmer = useMutation({
		mutationFn: ({ id, at }: { id: string; at?: string }) =>
			whatsappApi.schedule(id, at),
		onSuccess: () => {
			toast.success("Campagne programmée");
			rafraichir();
		},
		onError: echec,
	});

	const annuler = useMutation({
		mutationFn: (id: string) => whatsappApi.cancel(id),
		onSuccess: () => {
			toast.success("Campagne déprogrammée");
			rafraichir();
		},
		onError: echec,
	});

	const dupliquer = useMutation({
		mutationFn: (id: string) => whatsappApi.duplicate(id),
		onSuccess: () => {
			toast.success("Campagne dupliquée");
			rafraichir();
		},
		onError: echec,
	});

	const supprimer = useMutation({
		mutationFn: (id: string) => whatsappApi.remove(id),
		onSuccess: () => {
			toast.success("Campagne supprimée");
			rafraichir();
		},
		onError: echec,
	});

	const menu = (c: WaCampaign): ActionMenuEntry[] => {
		const entrees: ActionMenuEntry[] = [
			{
				label: "Dupliquer",
				icon: "copy",
				disabled: !peutEcrire,
				onClick: () => dupliquer.mutate(c.id),
			},
		];

		if (c.status === "draft") {
			entrees.unshift({
				label: "Envoyer maintenant",
				icon: "chat",
				disabled: !peutEnvoyer,
				onClick: () => {
					void confirm({
						title: "Envoyer cette campagne ?",
						message: `${nombre(c.recipients)} destinataire(s) · coût estimé ${francs(c.estimatedCost)}. WhatsApp facture chaque message envoyé.`,
						confirmLabel: "Envoyer",
						tone: "default",
					}).then((ok) => {
						if (ok) programmer.mutate({ id: c.id });
					});
				},
			});
		}

		if (c.status === "scheduled") {
			entrees.push({
				label: "Déprogrammer",
				icon: "clock",
				disabled: !peutEcrire,
				onClick: () => annuler.mutate(c.id),
			});
		}

		if (c.status !== "sent") {
			entrees.push("sep", {
				label: "Supprimer",
				icon: "trash",
				danger: true,
				disabled: !peutEcrire,
				onClick: () => {
					void confirm({
						title: "Supprimer cette campagne ?",
						message: `« ${c.name} » sera supprimée.`,
						confirmLabel: "Supprimer",
						tone: "danger",
					}).then((ok) => {
						if (ok) supprimer.mutate(c.id);
					});
				},
			});
		}

		return entrees;
	};

	const campagnes = data?.items ?? [];

	return (
		<>
			<PageHead
				title="WhatsApp Business"
				sub="Campagnes sur modèles approuvés par Meta"
				actions={
					peutEcrire && (
						<AdminButton variant="primary" onClick={() => setCreation(true)}>
							<AdminIcon n="plus" s={14} />
							Nouvelle campagne
						</AdminButton>
					)
				}
			/>

			{stats && !stats.connected && (
				<div style={{ marginBottom: 16 }}>
					<AlertBanner
						variant="warning"
						title="La connexion à WhatsApp Business ne répond pas"
						description="Le jeton Meta est absent ou expiré : aucun message ne pourra partir. Vérifiez la configuration du serveur avant de programmer un envoi."
					/>
				</div>
			)}

			{stats && (
				<div className="channel-grid" style={{ marginBottom: 18 }}>
					<div className="channel-card">
						<div className="ch-h">
							<div className="ch-ic" style={{ background: "#25D366" }}>
								<AdminIcon n="chat" s={18} />
							</div>
							<div>
								<div className="ch-name">Opt-in WhatsApp</div>
								<div className="ch-sub">Base contactable</div>
							</div>
						</div>
						<div className="ch-v">{nombre(stats.optin)}</div>
						<div className="muted" style={{ fontSize: 11 }}>
							Ont accepté d'être contactés sur WhatsApp
						</div>
					</div>
					<div className="channel-card">
						<div className="ch-h">
							<div className="ch-ic" style={{ background: "var(--a-forest)" }}>
								<AdminIcon n="trend" s={18} />
							</div>
							<div>
								<div className="ch-name">Taux de remise</div>
								<div className="ch-sub">Toutes campagnes</div>
							</div>
						</div>
						<div className="ch-v">
							{stats.deliveryRate.toFixed(1)}
							<small style={{ fontSize: 14, color: "var(--a-ink-mute)" }}>%</small>
						</div>
						<div className="muted" style={{ fontSize: 11 }}>
							{nombre(stats.sent)} envoyés · {nombre(stats.failed)} échecs
						</div>
					</div>
					<div className="channel-card">
						<div className="ch-h">
							<div className="ch-ic" style={{ background: "var(--a-terracotta)" }}>
								<AdminIcon n="card" s={18} />
							</div>
							<div>
								<div className="ch-name">Coût par message</div>
								<div className="ch-sub">Tarif marketing</div>
							</div>
						</div>
						<div className="ch-v">{francs(stats.pricePerMessage)}</div>
						<div className="muted" style={{ fontSize: 11 }}>
							Une campagne de 10 000 envois coûte{" "}
							{francs(stats.pricePerMessage * 10000)}
						</div>
					</div>
				</div>
			)}

			<Card>
				<div className="table-wrap" style={{ margin: 0 }}>
					<table className="table">
						<thead>
							<tr>
								<th>Campagne</th>
								<th>Modèle</th>
								<th>Cible</th>
								<th className="r">Envoyés</th>
								<th className="r">Coût</th>
								<th>Quand</th>
								<th>Statut</th>
								<th />
							</tr>
						</thead>
						<tbody>
							{isLoading && (
								<tr>
									<td colSpan={8}>
										<div className="empty">Chargement…</div>
									</td>
								</tr>
							)}
							{!isLoading && campagnes.length === 0 && (
								<tr>
									<td colSpan={8}>
										<div className="empty">Aucune campagne WhatsApp.</div>
									</td>
								</tr>
							)}
							{campagnes.map((c) => {
								const st = WA_STATUS_LABELS[c.status];
								return (
									<tr key={c.id}>
										<td>
											<strong style={{ fontSize: 12.5 }}>{c.name}</strong>
										</td>
										<td className="mono muted" style={{ fontSize: 11 }}>
											{c.templateName}
										</td>
										<td className="muted">{c.segmentLabel ?? "Tous opt-in"}</td>
										<td className="r tn">
											{c.sentCount > 0
												? nombre(c.sentCount)
												: nombre(c.recipients)}
										</td>
										<td className="r tn">{francs(c.estimatedCost)}</td>
										<td className="muted" style={{ fontSize: 11 }}>
											{dateHeure(c.sentAt ?? c.scheduledAt)}
										</td>
										<td>
											<Tag variant={st.variant}>{st.label}</Tag>
											{c.lastError && (
												<div
													className="muted"
													style={{ fontSize: 10.5, marginTop: 2 }}
												>
													{c.lastError}
												</div>
											)}
										</td>
										<td className="right">
											<ActionMenu items={menu(c)} />
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>
			</Card>

			<p
				className="muted"
				style={{ fontSize: 11.5, marginTop: 12, lineHeight: 1.6 }}
			>
				Les réponses entrantes arrivent dans le module Support, pas ici : une
				conversation ouverte par une campagne reste une conversation client.
			</p>

			{creation && (
				<CreationModal
					saving={creer.isPending}
					onClose={() => setCreation(false)}
					onSubmit={(p) => creer.mutate(p)}
				/>
			)}
		</>
	);
}

function CreationModal({
	onClose,
	onSubmit,
	saving,
}: {
	onClose: () => void;
	onSubmit: (p: {
		name: string;
		templateName: string;
		templateParams?: string[];
		segmentId?: string;
	}) => void;
	saving: boolean;
}) {
	const { data: segments } = useSegments();
	const [nom, setNom] = useState("");
	const [modele, setModele] = useState("");
	const [params, setParams] = useState("");
	const [segment, setSegment] = useState("");

	return (
		<Modal
			title="Nouvelle campagne WhatsApp"
			subtitle="Seuls les modèles approuvés par Meta peuvent être envoyés"
			onClose={onClose}
			width={540}
		>
			<FormRow label="Nom de la campagne" helper="Usage interne" required>
				<input
					className="aform-i"
					value={nom}
					onChange={(e) => setNom(e.target.value)}
					placeholder="Promo fête des pères"
				/>
			</FormRow>
			<FormRow
				label="Nom du modèle Meta"
				helper="Exactement celui approuvé dans Business Manager"
				required
			>
				<input
					className="aform-i mono"
					value={modele}
					onChange={(e) => setModele(e.target.value)}
					placeholder="marketing_collection"
				/>
			</FormRow>
			<FormRow
				label="Paramètres du modèle"
				helper="Dans l'ordre, séparés par des virgules. {{first_name}} accepté."
			>
				<input
					className="aform-i"
					value={params}
					onChange={(e) => setParams(e.target.value)}
					placeholder="{{first_name}}, -30%, dimanche"
				/>
			</FormRow>
			<FormRow label="Cible">
				<select
					className="aform-i"
					value={segment}
					onChange={(e) => setSegment(e.target.value)}
				>
					<option value="">Tous les opt-in WhatsApp</option>
					{(segments ?? []).map((s) => (
						<option key={s.id} value={s.id}>
							{s.name}
						</option>
					))}
				</select>
			</FormRow>
			<ModalFooter>
				<AdminButton variant="ghost" onClick={onClose}>
					Annuler
				</AdminButton>
				<AdminButton
					variant="primary"
					disabled={nom.trim().length < 3 || modele.trim().length < 3 || saving}
					onClick={() =>
						onSubmit({
							name: nom.trim(),
							templateName: modele.trim(),
							templateParams: params
								.split(",")
								.map((p) => p.trim())
								.filter(Boolean),
							segmentId: segment || undefined,
						})
					}
				>
					Créer le brouillon
				</AdminButton>
			</ModalFooter>
		</Modal>
	);
}
